import { useCallback, useEffect, useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, Archive, ArchiveRestore, ArrowLeft, Send } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Textarea,
} from "@alight/ui";
import {
  adminArchive,
  adminGetMessage,
  adminReopen,
  adminReply,
  ApiError,
  type Message,
  type MessageReply,
} from "../../lib/api";
import { formatDate, TopicBadge, StatusBadge } from "../../components/admin-bits";

export const Route = createFileRoute("/admin/messages/$id")({
  component: MessageDetail,
});

function MessageDetail() {
  const { id } = Route.useParams();

  const [message, setMessage] = useState<Message | null>(null);
  const [replies, setReplies] = useState<MessageReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await adminGetMessage(id);
      setMessage(data.message);
      setReplies(data.replies);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load this message.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  return (
    <section className="space-y-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 rounded text-sm font-semibold text-muted transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to inbox
      </Link>

      {loading && (
        <div className="rounded-card border border-border bg-card px-4 py-10 text-center text-sm text-muted">
          Loading message…
        </div>
      )}

      {!loading && error && (
        <div
          role="alert"
          className="rounded-card border border-accent/40 bg-accent/10 px-4 py-3 text-sm text-ink"
        >
          Couldn&apos;t load this message: {error}{" "}
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              void load();
            }}
            className="font-semibold text-primary underline-offset-2 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && message && (
        <>
          <MessageCard message={message} onChanged={load} />
          <RepliesThread replies={replies} />
          <ReplyComposer message={message} onSent={load} />
        </>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * Original message + archive/reopen
 * ------------------------------------------------------------------------- */

function MessageCard({ message, onChanged }: { message: Message; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const archived = message.status === "archived";

  async function toggleArchive() {
    setBusy(true);
    setActionError(null);
    try {
      if (archived) await adminReopen(message.id);
      else await adminArchive(message.id);
      await onChanged();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Action failed — please retry.");
    } finally {
      setBusy(false);
    }
  }

  const fields: Array<[string, string]> = [
    ["Reference", message.ref],
    ["Received", formatDate(message.created_at)],
    ["Name", message.name?.trim() || "Anonymous"],
    ["Email", message.email?.trim() || "—"],
    ["Phone", message.phone?.trim() || "—"],
    ["Subject", message.subject?.trim() || "—"],
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div className="space-y-2">
          <CardTitle className="font-heading text-xl text-ink">
            {message.subject?.trim() || `Message ${message.ref}`}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <TopicBadge topic={message.topic} />
            <StatusBadge status={message.status} />
            {message.wants_updates === 1 && <Badge variant="outline">Wants email updates</Badge>}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void toggleArchive()}
          disabled={busy}
        >
          {archived ? (
            <>
              <ArchiveRestore className="h-4 w-4" aria-hidden />
              {busy ? "Reopening…" : "Reopen"}
            </>
          ) : (
            <>
              <Archive className="h-4 w-4" aria-hidden />
              {busy ? "Archiving…" : "Archive"}
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {actionError && (
          <p role="alert" className="rounded-card border border-accent/40 bg-accent/10 px-3 py-2 text-sm text-ink">
            {actionError}
          </p>
        )}
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-3">
          {fields.map(([label, value]) => (
            <div key={label} className="flex flex-col">
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
              <dd className="break-words text-sm text-ink">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Message</div>
          <p className="whitespace-pre-wrap rounded-card border border-border bg-paper p-3 text-sm text-ink">
            {message.body}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------------------
 * Replies thread
 * ------------------------------------------------------------------------- */

function RepliesThread({ replies }: { replies: MessageReply[] }) {
  if (replies.length === 0) return null;
  return (
    <div className="space-y-3">
      <h2 className="font-heading text-lg font-bold text-ink">
        {replies.length === 1 ? "1 reply" : `${replies.length} replies`}
      </h2>
      <ol className="space-y-3">
        {replies.map((r) => (
          <li key={r.id} className="rounded-card border border-border bg-card p-4 shadow-soft">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-ink">{r.sent_by}</p>
              <div className="flex items-center gap-2">
                {r.emailed === 0 && (
                  <Badge variant="error" className="gap-1">
                    <AlertTriangle className="h-3 w-3" aria-hidden />
                    Email failed to send
                  </Badge>
                )}
                <span className="text-xs text-muted">{formatDate(r.created_at)}</span>
              </div>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-ink">{r.body}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Reply composer
 * ------------------------------------------------------------------------- */

function ReplyComposer({ message, onSent }: { message: Message; onSent: () => Promise<void> }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const hasEmail = Boolean(message.email?.trim());
  const phone = message.phone?.trim();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (body.trim() === "" || !hasEmail) return;
    setSending(true);
    setSendError(null);
    setNotice(null);
    try {
      const result = await adminReply(message.id, body.trim());
      setBody("");
      setNotice(
        result.emailed
          ? "Reply sent and emailed."
          : "Reply saved, but the email could not be sent — you may want to follow up another way.",
      );
      await onSent();
    } catch (err) {
      setSendError(err instanceof ApiError ? err.message : "Couldn't send the reply — please retry.");
    } finally {
      setSending(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading text-lg text-ink">Reply</CardTitle>
      </CardHeader>
      <CardContent>
        {hasEmail ? (
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="text-sm text-muted">
              Your reply is saved to this thread and emailed to{" "}
              <span className="font-medium text-ink">{message.email}</span>.
            </p>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              required
              disabled={sending}
              placeholder="Write your reply…"
              aria-label="Reply"
            />
            {sendError && (
              <p role="alert" className="rounded-card border border-accent/40 bg-accent/10 px-3 py-2 text-sm text-ink">
                {sendError}
              </p>
            )}
            {notice && (
              <p role="status" className="rounded-card border border-success/30 bg-success/10 px-3 py-2 text-sm text-ink">
                {notice}
              </p>
            )}
            <Button type="submit" disabled={sending || body.trim() === ""}>
              <Send className="h-4 w-4" aria-hidden />
              {sending ? "Sending…" : "Send reply"}
            </Button>
          </form>
        ) : (
          <div className="rounded-card border border-border bg-paper p-4 text-sm text-muted">
            <p>
              This message was left without an email address, so a reply can&apos;t be
              sent from here.
            </p>
            {phone ? (
              <p className="mt-2">
                They did leave a phone number — consider calling or texting{" "}
                <a href={`tel:${phone.replace(/\s+/g, "")}`} className="font-semibold text-primary hover:underline">
                  {phone}
                </a>{" "}
                to follow up.
              </p>
            ) : (
              <p className="mt-2">No contact details were provided, so no follow-up is possible.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
